import {announce} from '@github-ui/aria-live'
import {useAnalytics} from '@github-ui/use-analytics'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Dialog, Flash, type ButtonProps} from '@primer/react'
import {useId, useRef, useState} from 'react'

import {StopIcon} from '@primer/octicons-react'

import type {StackSectionStackEntry} from '../../../helpers/merge-box-status-calculator/stack-section-types'
import {useDisableAutoMergeMutation} from '../../../hooks/mutations/use-disable-auto-merge-mutation'
import {StackEntryPreviewList} from '../stack-section/StackEntryPreviewList'
import {MergeSectionActions} from './MergeSectionActions'

import styles from './DisableAutoMerge.module.css'

const NO_ENTRIES: readonly StackSectionStackEntry[] = []

type DisableAutoMergeProps = {
  /** Stacked pull requests whose auto-merge is turned off with this one. When present, disabling asks to confirm. */
  autoMergeRangeEntries?: readonly StackSectionStackEntry[]
  baseRefName?: string
  confirmation?: 'inline' | 'dialog'
  isMergeQueueEnabled?: boolean
  isStacked?: boolean
  viewerCanDisableAutoMerge: boolean
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  /** Moves focus to the merge control that replaces this one once auto-merge is disabled. */
  setShouldFocusPrimaryMergeButton?: (shouldFocus: boolean) => void
  buttonSize?: ButtonProps['size']
  instructions?: React.ReactNode
}

// Only stacked pull requests ask to confirm, so only they send open and cancel events.
const STACKED_DISABLE_AUTO_MERGE_ANALYTICS_EVENTS = {
  direct: {
    open: 'stacks_direct_merge_section.disable_auto_merge_click',
    confirm: 'stacks_direct_merge_section.disable_auto_merge',
    cancel: 'stacks_direct_merge_section.cancel_disable_auto_merge',
  },
  mergeQueue: {
    open: 'stacks_merge_queue_merge_section.disable_auto_merge_click',
    confirm: 'stacks_merge_queue_merge_section.disable_auto_merge',
    cancel: 'stacks_merge_queue_merge_section.cancel_disable_auto_merge',
  },
} as const

function focusOnMount(element: HTMLElement | null) {
  element?.focus()
}

function focusAndRevealOnMount(element: HTMLElement | null) {
  element?.focus()
  element?.scrollIntoView({behavior: 'smooth', block: 'nearest'})
}

/**
 * Renders the "disable auto merge" button when auto merge is currently enabled but the PR hasn't merged or been
 * added to the merge queue yet
 */
export function DisableAutoMerge({
  autoMergeRangeEntries = NO_ENTRIES,
  baseRefName = '',
  buttonSize,
  confirmation = 'inline',
  isMergeQueueEnabled = false,
  isStacked = false,
  viewerCanDisableAutoMerge,
  handleConfirmingMergeInfo,
  setShouldFocusPrimaryMergeButton,
  instructions,
}: DisableAutoMergeProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isConfirming, setIsConfirming] = useState(false)
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState(false)
  const questionId = useId()
  const confirmButtonRef = useRef<HTMLButtonElement>(null)

  const {mutate: disableAutoMerge, isPending} = useDisableAutoMergeMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
      // Focus stays on the confirm button, so the error shown above it has to be announced.
      if (isConfirming) announce(e.message)
    },
    // Leave the confirming state before the refetch swaps this control for the enable control,
    // so the enable control doesn't open its own confirmation.
    onSuccess: () => {
      handleConfirmingMergeInfo(false)
      // The focused confirm button is about to be removed, so move focus to the control that replaces it.
      if (isConfirming) setShouldFocusPrimaryMergeButton?.(true)
    },
  })

  const requiresConfirmation = isStacked && autoMergeRangeEntries.length > 0
  const pullRequestCount = autoMergeRangeEntries.length

  const stackedAnalyticsEvents = isMergeQueueEnabled
    ? STACKED_DISABLE_AUTO_MERGE_ANALYTICS_EVENTS.mergeQueue
    : STACKED_DISABLE_AUTO_MERGE_ANALYTICS_EVENTS.direct

  const sendDisableAnalyticsEvent = () => {
    sendAnalyticsEvent(
      isStacked ? stackedAnalyticsEvents.confirm : 'auto_merge_section.disable_auto_merge',
      'MERGEBOX_AUTO_MERGE_DISABLE_BUTTON',
    )
  }

  const handleDisableAutoMerge = () => {
    if (isPending) return
    setErrorMessage(undefined)

    if (requiresConfirmation) {
      setIsConfirming(true)
      handleConfirmingMergeInfo(true)
      sendAnalyticsEvent(stackedAnalyticsEvents.open, 'MERGEBOX_AUTO_MERGE_DISABLE_BUTTON')
      return
    }

    handleConfirmingMergeInfo(false)
    disableAutoMerge()
    sendDisableAnalyticsEvent()
  }

  const handleConfirmDisableAutoMerge = () => {
    if (isPending) return
    setErrorMessage(undefined)
    disableAutoMerge()
    sendDisableAnalyticsEvent()
  }

  const handleCancel = () => {
    setIsConfirming(false)
    setErrorMessage(undefined)
    setShouldRestoreFocus(true)
    handleConfirmingMergeInfo(false)
    sendAnalyticsEvent(stackedAnalyticsEvents.cancel, 'MERGEBOX_AUTO_MERGE_CANCEL_CONFIRMATION_BUTTON')
  }

  const renderErrorFlash = (className: string) =>
    errorMessage && (
      <Flash className={className} variant="danger">
        <StopIcon className="mr-2" />
        {errorMessage}
      </Flash>
    )

  if (isConfirming) {
    const confirmationContent = (
      <div>
        {renderErrorFlash('tmp-mb-3')}
        <p id={questionId} className={styles.ConfirmationQuestion}>
          Are you sure you want to disable auto-merge for {pullRequestCount}{' '}
          {pullRequestCount === 1 ? 'pull request' : 'pull requests'}?
        </p>
        <MergeSectionActions className="tmp-mt-3" inline>
          <MergeSectionActions.Slot>
            <Button
              // Primer's Dialog places initial focus itself, so only move focus manually when rendered inline.
              ref={confirmation === 'dialog' ? confirmButtonRef : focusAndRevealOnMount}
              aria-describedby={questionId}
              className="width-full"
              count={pullRequestCount > 1 ? pullRequestCount : undefined}
              size={confirmation === 'dialog' ? undefined : buttonSize}
              onClick={handleConfirmDisableAutoMerge}
              loading={isPending}
              loadingAnnouncement="Disabling auto-merge"
            >
              Disable auto-merge
            </Button>
          </MergeSectionActions.Slot>
          <MergeSectionActions.Slot>
            <Button size={confirmation === 'dialog' ? undefined : buttonSize} onClick={handleCancel}>
              Cancel
            </Button>
          </MergeSectionActions.Slot>
        </MergeSectionActions>
        <StackEntryPreviewList
          aria-label="Pull requests to disable auto-merge"
          baseRefName={baseRefName}
          entries={autoMergeRangeEntries}
        />
      </div>
    )

    return confirmation === 'dialog' ? (
      <Dialog title="Disable auto-merge" onClose={handleCancel} initialFocusRef={confirmButtonRef}>
        {confirmationContent}
      </Dialog>
    ) : (
      confirmationContent
    )
  }

  return (
    <>
      {renderErrorFlash('tmp-mx-3 my-2')}
      <MergeSectionActions>
        <MergeSectionActions.Slot>
          <Button
            ref={shouldRestoreFocus ? focusOnMount : undefined}
            size={buttonSize}
            disabled={!viewerCanDisableAutoMerge}
            onClick={handleDisableAutoMerge}
            loading={isPending}
            loadingAnnouncement="Disabling auto-merge"
          >
            Disable auto-merge
          </Button>
        </MergeSectionActions.Slot>
        {!!instructions && <MergeSectionActions.Slot>{instructions}</MergeSectionActions.Slot>}
      </MergeSectionActions>
    </>
  )
}
